(() => {
  'use strict';

  const GARAGE_KEY = 'trmSelectedVehicleV1';
  const LEGACY_VEHICLE_KEY = 'trmExactVehicleV2';
  const PDP_KEY = 'trmFinderPdpContextV11';
  const PROFILE_KEY = 'trmBedCoverProfileV14';
  const PRIVATE_KEY = 'trmBedCoverPrivateSessionV14';
  const REQUEST_KEY = 'trmFinderRequestV14';
  const BED_COVER_FALLBACK_IMAGE = 'https://cdn.shopify.com/s/files/1/0755/5933/8020/files/ag4a1603_1_1.jpg?v=1786359466';
  const productCache = new Map();

  const read = (storage, key) => {
    try { return JSON.parse(storage.getItem(key) || 'null'); } catch (_) { return null; }
  };
  const text = (value) => String(value == null ? '' : value).trim();
  const number = (value) => {
    if (value === null || value === undefined || value === '' || value === 'other') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const currentVehicle = () =>
    read(localStorage, GARAGE_KEY) ||
    read(sessionStorage, LEGACY_VEHICLE_KEY) ||
    read(localStorage, LEGACY_VEHICLE_KEY);
  const vehicleKey = (vehicle) =>
    vehicle?.exactVehicleKey || vehicle?.fitmentKey || vehicle?.key || '';
  const vehicleLabel = (vehicle) =>
    vehicle?.label ||
    [vehicle?.year, vehicle?.make, vehicle?.model, vehicle?.trim].filter(Boolean).join(' ') ||
    'Choose your truck to begin';
  const collectionVehicleKey = (vehicle) => text(vehicleKey(vehicle))
    .toLowerCase()
    .replace(/\+/g, '-plus-')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const genericBrowseTarget = (finderKey, vehicle) => {
    const key = collectionVehicleKey(vehicle);
    const label = vehicleLabel(vehicle);
    if (finderKey === 'WHEEL_TIRE') return {
      href: key ? `/collections/${key}-wheels-tires` : '/collections/wheels-forged',
      text: key ? `I’d rather browse all wheels & tires for ${label}` : 'I’d rather browse all wheels & tires',
      note: key ? `Filtered to ${label}; your Garage truck stays saved.` : 'Choose a Garage truck for the vehicle-filtered collection.'
    };
    if (finderKey === 'LIGHTING') return {
      href: key ? `/collections/${key}-accessories-lighting` : '/collections/accessories-lighting',
      text: key ? `I’d rather browse all accessories & lighting for ${label}` : 'I’d rather browse all accessories & lighting',
      note: key ? `Filtered to ${label}; your Garage truck stays saved.` : 'Choose a Garage truck for the vehicle-filtered collection.'
    };
    return {
      href: '/collections/recovery-winches',
      text: 'I’d rather browse all recovery & winch products',
      note: key ? `${label} stays saved in your Garage; confirm mounting and capacity before ordering.` : 'Choose a Garage truck before ordering so mounting can be confirmed.'
    };
  };
  const updateGenericBrowse = (root, finderKey) => {
    const target = genericBrowseTarget(finderKey, currentVehicle());
    root.querySelectorAll('[data-trm-v11-generic-browse]').forEach((link) => {
      link.href = target.href;
      if (link.firstChild?.nodeType === Node.TEXT_NODE) link.firstChild.textContent = `${target.text} `;
      else link.setAttribute('aria-label', target.text);
    });
    root.querySelectorAll('[data-trm-v11-generic-browse-note]').forEach((note) => { note.textContent = target.note; });
  };
  const safePdpContext = (finderKey) => {
    const context = read(sessionStorage, PDP_KEY);
    return context?.finderKey === finderKey ? context : null;
  };
  const uniqueBy = (items, keyFn) => {
    const seen = new Set();
    return items.filter((item) => {
      const key = keyFn(item);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const humanize = (value) => text(value)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace(/\b4wd\b/i, '4WD')
    .replace(/\bawd\b/i, 'AWD')
    .replace(/\bfwd\b/i, 'FWD')
    .replace(/\brwd\b/i, 'RWD');

  const bedCatalog = () => Array.isArray(window.TRM_RCBED_COVERS_V13)
    ? window.TRM_RCBED_COVERS_V13
    : [];

  function parseApplication(key) {
    const parts = text(key).split('|');
    if (parts.length < 6) return null;
    return {
      key,
      year: parts[0],
      make: parts[1],
      model: parts[2],
      trim: parts[3],
      engine: parts[4],
      drive: parts[5]
    };
  }

  function exactApplications() {
    return uniqueBy(
      bedCatalog().flatMap((item) => item.fitmentKeys || []).map(parseApplication).filter(Boolean),
      (item) => item.key
    );
  }

  function replaceOptions(select, values, placeholder, selected = '') {
    if (!select) return;
    const options = [new Option(placeholder, '')];
    values.forEach((value) => options.push(new Option(humanize(value), value)));
    select.replaceChildren(...options);
    select.disabled = values.length === 0;
    if (selected && values.includes(selected)) select.value = selected;
  }

  function exactVehicleLabel(fields) {
    return [fields.exact_year, humanize(fields.exact_make), humanize(fields.exact_model), humanize(fields.exact_trim), humanize(fields.exact_drive)]
      .filter(Boolean)
      .join(' ');
  }

  function setupExactVehicle(root, form) {
    const yearSelect = root.querySelector('[data-trm-v11-year]');
    const makeSelect = root.querySelector('[data-trm-v11-make]');
    const modelSelect = root.querySelector('[data-trm-v11-model]');
    const trimSelect = root.querySelector('[data-trm-v11-trim]');
    const driveSelect = root.querySelector('[data-trm-v11-drive]');
    const exactInput = root.querySelector('[data-trm-v11-exact-key]');
    const bedSelect = root.querySelector('[data-trm-v11-bed]');
    if (!yearSelect || !makeSelect || !modelSelect || !trimSelect || !driveSelect || !exactInput) return null;

    const applications = exactApplications();
    const uniqueValues = (items, key) => [...new Set(items.map((item) => item[key]).filter(Boolean))];
    const years = uniqueValues(applications, 'year').sort((a, b) => Number(b) - Number(a));
    const makes = uniqueValues(applications, 'make').sort();
    replaceOptions(yearSelect, years, 'Choose year');
    replaceOptions(makeSelect, makes, 'Choose make');

    const updateBedLengths = () => {
      if (!bedSelect) return;
      const previous = bedSelect.value;
      const matches = exactInput.value
        ? bedCatalog().filter((item) => Array.isArray(item.fitmentKeys) && item.fitmentKeys.includes(exactInput.value))
        : [];
      const lengths = uniqueBy(matches, (item) => String(item.bedLengthIn))
        .map((item) => ({ value: String(item.bedLengthIn), label: `${item.bedLength || humanize(item.bedLengthIn)} (${item.bedLengthIn} in)` }))
        .sort((a, b) => Number(a.value) - Number(b.value));
      const options = [new Option(lengths.length ? 'Choose your bed length' : 'Confirm the exact truck first', '')];
      lengths.forEach((item) => options.push(new Option(item.label, item.value)));
      if (lengths.length) options.push(new Option('Other measured length', 'other'));
      bedSelect.replaceChildren(...options);
      bedSelect.disabled = lengths.length === 0;
      if (lengths.some((item) => item.value === previous)) bedSelect.value = previous;
    };

    const updateExactKey = () => {
      const match = applications.find((item) =>
        item.year === yearSelect.value &&
        item.make === makeSelect.value &&
        item.model === modelSelect.value &&
        item.trim === trimSelect.value &&
        item.drive === driveSelect.value
      );
      exactInput.value = match?.key || '';
      updateBedLengths();
    };

    const updateDrive = (preserve = '') => {
      const filtered = applications.filter((item) =>
        (!yearSelect.value || item.year === yearSelect.value) &&
        item.make === makeSelect.value &&
        item.model === modelSelect.value &&
        item.trim === trimSelect.value
      );
      replaceOptions(driveSelect, uniqueValues(filtered, 'drive').sort(), 'Choose drivetrain', preserve || driveSelect.value);
      updateExactKey();
    };

    const updateTrim = (preserveTrim = '', preserveDrive = '') => {
      const filtered = applications.filter((item) =>
        (!yearSelect.value || item.year === yearSelect.value) &&
        item.make === makeSelect.value &&
        item.model === modelSelect.value
      );
      replaceOptions(trimSelect, uniqueValues(filtered, 'trim').sort(), 'Choose trim', preserveTrim || trimSelect.value);
      updateDrive(preserveDrive);
    };

    const updateModel = (preserveModel = '', preserveTrim = '', preserveDrive = '') => {
      const filtered = applications.filter((item) =>
        (!yearSelect.value || item.year === yearSelect.value) &&
        item.make === makeSelect.value
      );
      replaceOptions(modelSelect, uniqueValues(filtered, 'model').sort(), 'Choose model', preserveModel || modelSelect.value);
      updateTrim(preserveTrim, preserveDrive);
    };

    const prefill = (preferGarage = false) => {
      const garage = currentVehicle();
      const profile = read(sessionStorage, PROFILE_KEY);
      const normalizeIdentity = (value) => text(value).toLowerCase().replace(/[^a-z0-9]/g, '');
      const garageText = `${vehicleKey(garage)} ${vehicleLabel(garage)} ${garage?.exactYear || ''} ${garage?.drivetrain || ''}`.toLowerCase();
      const garageIdentity = normalizeIdentity(garageText);
      const inferredMakeByModel = {
        f150: 'ford', f250: 'ford', f250superduty: 'ford', f350: 'ford', f350superduty: 'ford', f450: 'ford', f550: 'ford', ranger: 'ford', maverick: 'ford',
        silverado1500: 'chevrolet', silverado2500hd: 'chevrolet', silverado3500hd: 'chevrolet', colorado: 'chevrolet',
        sierra1500: 'gmc', sierra2500hd: 'gmc', sierra3500hd: 'gmc', canyon: 'gmc',
        ram1500: 'ram', ram2500: 'ram', ram3500: 'ram',
        tundra: 'toyota', tacoma: 'toyota',
        wrangler: 'jeep', gladiator: 'jeep'
      };
      const inferredMake = Object.entries(inferredMakeByModel)
        .sort((a, b) => b[0].length - a[0].length)
        .find(([model]) => garageIdentity.includes(model))?.[1] || '';
      const profileExact = parseApplication(profile?.exactVehicleKey || '');
      const profileMatchesGarage = !vehicleKey(garage) || Boolean(
        profileExact &&
        profile?.garageVehicleKey === vehicleKey(garage) &&
        [profileExact.year, profileExact.make, profileExact.model]
          .every((value) => garageIdentity.includes(normalizeIdentity(value)))
      );
      const canReuseProfile = !preferGarage && profile?.exactVehicleKey &&
        profileMatchesGarage;
      const exact = parseApplication(canReuseProfile ? profile.exactVehicleKey : '');
      if (exact) {
        yearSelect.value = exact.year;
        makeSelect.value = exact.make;
        updateModel(exact.model, exact.trim, exact.drive);
        return;
      }

      const year = years.find((value) => garageText.includes(value));
      const make = makes.find((value) => garageIdentity.includes(normalizeIdentity(value))) ||
        makes.find((value) => normalizeIdentity(value) === inferredMake);
      if (!make) return;
      if (year) yearSelect.value = year;
      makeSelect.value = make;
      updateModel();
      const models = Array.from(modelSelect.options).map((option) => option.value).filter(Boolean);
      const model = models.sort((a, b) => b.length - a.length).find((value) => garageIdentity.includes(normalizeIdentity(value)));
      if (model) {
        modelSelect.value = model;
        updateTrim();
      }
    };

    yearSelect.addEventListener('change', () => updateModel());
    makeSelect.addEventListener('change', () => updateModel());
    modelSelect.addEventListener('change', () => updateTrim());
    trimSelect.addEventListener('change', () => updateDrive());
    driveSelect.addEventListener('change', updateExactKey);
    prefill();

    return {
      prefill,
      updateBedLengths,
      exactKey: () => exactInput.value,
      exactLabel: () => exactVehicleLabel(Object.fromEntries(new FormData(form)))
    };
  }

  function selectedBedLength(fields) {
    return fields.bed_length_in === 'other'
      ? number(fields.bed_length_other)
      : number(fields.bed_length_in);
  }

  function productJsonUrl(productUrl) {
    try {
      const parsed = new URL(productUrl, window.location.origin);
      if (!parsed.pathname.startsWith('/products/')) return null;
      return `${parsed.pathname.replace(/\/$/, '')}.js`;
    } catch (_) {
      return null;
    }
  }

  async function loadProduct(item) {
    const url = productJsonUrl(item?.url);
    if (!url) return null;
    if (!productCache.has(url)) {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timeout = window.setTimeout(() => controller?.abort(), 5000);
      productCache.set(url, fetch(url, { credentials: 'same-origin', signal: controller?.signal })
        .then((response) => response.ok ? response.json() : null)
        .catch(() => null)
        .finally(() => window.clearTimeout(timeout)));
    }
    return productCache.get(url);
  }

  function recommendationScore(item, use) {
    const security = text(item.securityClass).toLowerCase();
    const style = text(item.functionClass).toLowerCase();
    let score = 0;
    if (use === 'daily') score += security === 'soft' ? 8 : 5;
    if (use === 'security') score += security === 'hard' ? 12 : 1;
    if (use === 'max_access') score += style === 'roll-up' ? 11 : style === 'tri-fold' ? 6 : 3;
    if (use === 'work') score += security === 'hard' ? 9 : 4;
    if (use === 'towing') score += style === 'roll-up' ? 9 : style === 'tri-fold' ? 6 : 3;
    if (use === 'rack_toolbox') score += style === 'roll-up' ? 7 : 4;
    return score;
  }

  function recommendationCopy(item, use) {
    const hard = text(item.securityClass).toLowerCase() === 'hard';
    const style = text(item.functionClass).toLowerCase();
    const styleName = style === 'tri-fold' ? 'tri-fold design' : style === 'roll-up' ? 'roll-up design' : style || 'cover design';
    const reasons = {
      daily: `A practical ${styleName} for routine weather protection and everyday bed access.`,
      security: hard
        ? `The hard-panel ${styleName} makes this one of the stronger choices for tools and valuable cargo.`
        : `This option is easy to use, but a hard-panel cover may be the better choice when security is the top priority.`,
      max_access: style === 'roll-up'
        ? 'The roll-up layout gets out of the way quickly when you need to load tall or bulky cargo.'
        : 'A folding layout balances useful bed access with solid everyday coverage.',
      work: hard
        ? 'Hard construction suits frequent use and adds a firmer barrier over work gear.'
        : 'A straightforward, lighter option when easy opening and value matter more than a hard shell.',
      towing: style === 'roll-up'
        ? 'The roll-up layout is a sensible starting point when hitch access and an open bed matter.'
        : 'This style may work for towing, but hitch position and required open-bed clearance should be checked.',
      rack_toolbox: 'This matches the truck and bed size; rack or toolbox rail space still needs a quick compatibility check.'
    };
    return reasons[use] || 'This option matches the selected truck and measured bed length.';
  }

  function tradeoffCopy(item, use, interaction) {
    const hard = text(item.securityClass).toLowerCase() === 'hard';
    const style = text(item.functionClass).toLowerCase();
    if (interaction && interaction !== 'none') {
      return 'Before ordering: confirm the rail space with the existing rack, toolbox, factory rail or hitch hardware.';
    }
    if (use === 'security' && !hard) return 'Tradeoff: soft construction favors value and easy operation over maximum cargo security.';
    if (style === 'tri-fold') return 'Tradeoff: folded panels can cover part of the bed or rear-window area, depending on the design.';
    if (style === 'roll-up') return 'Tradeoff: roll-up covers prioritize bed access; material and security level vary by model.';
    return 'Check the product page for opening clearance, rail requirements and installation details.';
  }

  function formatMoney(cents) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value / 100);
  }

  function createResultCard(item, fields, rank) {
    const card = document.createElement('article');
    card.className = 'trm-v11__result-card';
    card.classList.add('is-loading');
    if (rank === 0) card.classList.add('is-best-match');

    const media = document.createElement('div');
    media.className = 'trm-v11__result-media';
    const image = document.createElement('img');
    image.alt = item.title || 'Recommended truck bed cover';
    image.loading = rank === 0 ? 'eager' : 'lazy';
    image.decoding = 'async';
    image.width = 640;
    image.height = 400;
    const availability = document.createElement('span');
    availability.className = 'trm-v11__availability';
    availability.textContent = 'Checking availability…';
    media.append(image, availability);

    const body = document.createElement('div');
    body.className = 'trm-v11__result-body';
    const meta = document.createElement('p');
    meta.className = 'trm-v11__result-meta';
    meta.textContent = rank === 0 ? 'Your best match' : `Strong alternative ${rank + 1}`;
    const title = document.createElement('h3');
    title.textContent = item.title || `Rough Country ${item.mpn || 'bed cover'}`;
    const specs = document.createElement('div');
    specs.className = 'trm-v11__result-specs';
    [
      item.bedLength || (item.bedLengthIn ? `${item.bedLengthIn} in bed` : ''),
      item.securityClass ? `${humanize(item.securityClass)} construction` : '',
      item.functionClass ? humanize(item.functionClass) : ''
    ].filter(Boolean).forEach((value) => {
      const chip = document.createElement('span');
      chip.textContent = value;
      specs.append(chip);
    });
    const reason = document.createElement('p');
    reason.className = 'trm-v11__result-reason';
    const reasonLabel = document.createElement('strong');
    reasonLabel.textContent = 'Why it ranks here';
    reason.append(reasonLabel, document.createTextNode(recommendationCopy(item, fields.primary_use)));
    const tradeoff = document.createElement('p');
    tradeoff.className = 'trm-v11__result-tradeoff';
    const tradeoffLabel = document.createElement('strong');
    tradeoffLabel.textContent = 'Know before you buy';
    tradeoff.append(tradeoffLabel, document.createTextNode(tradeoffCopy(item, fields.primary_use, fields.bed_interaction)));
    const footer = document.createElement('div');
    footer.className = 'trm-v11__result-footer';
    const price = document.createElement('span');
    price.className = 'trm-v11__price';
    price.textContent = 'See current price';
    const link = document.createElement('a');
    link.href = item.url;
    link.textContent = 'View cover';
    footer.append(price, link);
    body.append(meta, title, specs, reason, tradeoff, footer);
    card.append(media, body);

    let imageTimeout = 0;
    const markNoImage = () => {
      if (imageTimeout) window.clearTimeout(imageTimeout);
      card.classList.remove('is-loading');
      card.classList.add('has-no-image');
      image.removeAttribute('src');
      if (availability.parentElement !== body) body.prepend(availability);
    };
    const useImage = (url) => {
      if (!url) { markNoImage(); return; }
      if (imageTimeout) window.clearTimeout(imageTimeout);
      card.classList.remove('has-no-image');
      card.classList.add('is-loading');
      if (availability.parentElement !== media) media.append(availability);
      image.onload = () => {
        if (imageTimeout) window.clearTimeout(imageTimeout);
        card.classList.remove('is-loading');
      };
      image.onerror = () => markNoImage();
      image.src = url;
      imageTimeout = window.setTimeout(markNoImage, 5500);
    };
    imageTimeout = window.setTimeout(markNoImage, 5500);

    loadProduct(item).then((product) => {
      if (!product) {
        availability.textContent = 'Check current availability';
        markNoImage();
        return;
      }
      let imageUrl = [item.image, product.featured_image, ...(product.images || [])]
        .filter(Boolean)
        .find((url) => !/(warning|prop[-_ ]?65|cancer|reproduction|logo|brand[-_ ]?mark)/i.test(url));
      if (!imageUrl) {
        imageUrl = BED_COVER_FALLBACK_IMAGE;
        image.alt = 'Installed truck bed cover example; exact product image unavailable';
        card.classList.add('uses-fallback-image');
        const imageNote = document.createElement('small');
        imageNote.className = 'trm-v11__image-note';
        imageNote.textContent = 'Category image · verify exact product gallery';
        media.append(imageNote);
      }
      useImage(imageUrl);
      const availableNow = product.available || product.variants?.some((variant) => variant.available);
      availability.textContent = availableNow ? 'Available to order' : 'Currently unavailable';
      const money = formatMoney(product.price);
      if (money) price.textContent = money;
    });

    return card;
  }

  function findBedCoverMatches(vehicle, fields) {
    const key = text(fields.exact_vehicle_key) || vehicleKey(vehicle);
    const requestedLength = selectedBedLength(fields);
    const catalog = bedCatalog();
    if (!key || requestedLength == null || !catalog.length) return [];

    const matches = catalog.filter((item) =>
      Array.isArray(item.fitmentKeys) &&
      item.fitmentKeys.includes(key) &&
      Math.abs(number(item.bedLengthIn) - requestedLength) <= 0.5
    );

    return uniqueBy(matches, (item) => item.url || item.sku || item.mpn)
      .sort((a, b) => recommendationScore(b, fields.primary_use) - recommendationScore(a, fields.primary_use));
  }

  function renderBedCoverResults(root, matches, fields) {
    const panel = root.querySelector('[data-trm-v11-results]');
    const grid = root.querySelector('[data-trm-v11-result-grid]');
    if (!panel || !grid) return;
    panel.hidden = !matches.length;
    grid.replaceChildren(...matches.slice(0, 8).map((item, index) => createResultCard(item, fields, index)));
    const heading = root.querySelector('[data-trm-v11-results-title]');
    const count = root.querySelector('[data-trm-v11-result-count]');
    if (heading) heading.textContent = matches.length === 1 ? 'Your matching cover' : 'Your recommended covers';
    if (count) count.textContent = `${matches.length} match${matches.length === 1 ? '' : 'es'}`;
    const browse = root.querySelector('[data-trm-v11-bed-browse]');
    if (browse) {
      const label = exactVehicleLabel(fields);
      browse.textContent = label
        ? `I’d rather browse every bed cover — keep ${label} saved →`
        : 'I’d rather browse every bed cover — keep my truck saved →';
    }
  }

  function renderDecision(root, state, title, copy) {
    const panel = root.querySelector('[data-trm-v11-decision]');
    if (!panel) return;
    panel.hidden = false;
    panel.dataset.state = state;
    root.querySelector('[data-trm-v11-decision-title]').textContent = title;
    root.querySelector('[data-trm-v11-decision-copy]').textContent = copy;
    updateGenericBrowse(root, root.dataset.finderKey);
    panel.focus({ preventScroll: true });
  }

  function genericRecommendation(finderKey, fields) {
    if (finderKey === 'WHEEL_TIRE') {
      const tire = fields.tire_diameter_in === 'stock' ? 'the stock tire size' : fields.tire_diameter_in === 'other' ? 'a custom tire size' : `${fields.tire_diameter_in}-inch tires`;
      const width = fields.wheel_width === 'unsure' ? 'an advisor-selected wheel width' : `${fields.wheel_width}-inch-wide wheels`;
      const stance = { factory:'near-factory stance', flush:'a flush stance', mild_poke:'a mild-poke stance', aggressive:'an aggressive wide stance', unsure:'the safest verified stance options' }[fields.stance_preference] || 'the selected stance';
      return {
        title: 'Your wheel and tire fitment brief is ready',
        copy: `You’re targeting ${tire} on ${fields.wheel_diameter}-inch, ${width} with ${stance}. Final approval still checks bolt pattern, hub bore, offset or backspacing, brake clearance, load rating, true tire dimensions and full steering and suspension clearance for your saved truck.`
      };
    }
    if (finderKey === 'LIGHTING') {
      const beams = { distance: 'a long-range beam', balanced: 'a driving/combo beam', wide: 'a wide work beam', fog: 'a fog-focused beam' };
      return {
        title: 'Your lighting plan is ready',
        copy: `Start with ${beams[fields.beam_use] || 'the selected beam'} at the ${fields.mounting_location} location. The final kit should include the vehicle-specific mount plus the correct harness or switch interface.`
      };
    }
    const loaded = number(fields.loaded_weight_lbs);
    const minimum = loaded == null ? null : Math.ceil((loaded * 1.5) / 500) * 500;
    return {
      title: 'Your recovery starting point is ready',
      copy: minimum
        ? `For a loaded weight of ${loaded.toLocaleString('en-US')} lb, begin with at least a ${minimum.toLocaleString('en-US')}-lb winch class. Tex can help pair it with a rated mount, line and fairlead.`
        : 'Enter the loaded truck weight so we can calculate a practical winch-capacity starting point.'
    };
  }

  const chatInternals = (chat) => {
    const runtime = chat?.shadowRoot?.querySelector('shopify-agent-runtime');
    const root = runtime?.shadowRoot || chat?.shadowRoot;
    return {
      launcher: root?.querySelector('button[part="activator"], button[aria-label="Need help?"]') || chat?.shadowRoot?.querySelector('button[part="activator"], button[aria-label="Need help?"]'),
      panel: root?.querySelector('[role="dialog"]') || chat?.shadowRoot?.querySelector('[role="dialog"]')
    };
  };
  const openTexChat = () => {
    let chat = document.querySelector('shopify-chat');
    if (!chat) {
      chat = document.createElement('shopify-chat');
      chat.setAttribute('view', 'chat');
      document.body.appendChild(chat);
    }
    const before = chatInternals(chat);
    if (before.panel || before.launcher?.getAttribute('aria-expanded') === 'true') return true;
    try { chat.open?.(); } catch (_) {}
    const after = chatInternals(chat);
    if (after.launcher?.getAttribute('aria-expanded') !== 'true') after.launcher?.click();
    return Boolean(after.panel || after.launcher?.getAttribute('aria-expanded') === 'true' || chat.hasAttribute('open'));
  };
  const requestTexChat = (attempts = 20) => {
    if (openTexChat() || attempts <= 1) return;
    window.setTimeout(() => requestTexChat(attempts - 1), 200);
  };

  const LAB_COPY = {
    BED_COVER: {
      number: '01', label: 'YOUR COVER SHORTLIST', title: 'One truck. Three smart cover choices.',
      stages: [
        { type:'vehicle', name:'exact_vehicle_key', eyebrow:'Start with your truck', title:'Is this the truck we’re building?', copy:'We use your My Truck Garage selection first. Confirm the few missing details once—then the guide only shows covers built for that exact application.' },
        { name:'bed_length_in', eyebrow:'Step 2 · Bed size', title:'Which bed is behind the cab?', copy:'Choose the measured inside length. That single number separates look-alike covers that will not interchange.', bedHelp:true },
        { name:'primary_use', eyebrow:'Step 3 · Your life', title:'What earns the cover a place on your truck?', copy:'Pick the job it must do best. We will rank every verified match around that priority.', labels:{daily:'Daily weather protection',security:'Lock up tools & gear',max_access:'Open the whole bed fast',work:'Hard-use work truck',towing:'Tow with bed access',rack_toolbox:'Keep a rack or toolbox'}, meta:{daily:'Easy operation, protection and strong value',security:'Harder barrier over valuable cargo',max_access:'Make room for tall and bulky loads',work:'Built around frequent demanding use',towing:'Prioritize hitch and open-bed clearance',rack_toolbox:'Protect shared rail and mounting space'}, icons:{daily:'DAY',security:'LOCK',max_access:'OPEN',work:'WORK',towing:'TOW',rack_toolbox:'RACK'} },
        { name: 'bed_interaction', eyebrow: 'Final detail', title: 'What is already mounted in the bed?', copy: 'Rails, racks, toolboxes and hitches can compete for the same mounting space.', submit: 'Build my cover shortlist' }
      ]
    },
    WHEEL_TIRE: {
      number: '02', label: 'STANCE BLUEPRINT', title: 'Make the stance look intentional.',
      stages: [
        { type:'garage', name:'garage_vehicle', eyebrow:'Start with your truck', title:'Which truck gets the new stance?', copy:'We pulled your saved Garage vehicle into the build. Confirm it before choosing lift, tire and wheel targets.' },
        { name:'front_lift_in', eyebrow:'Suspension', title:'Where does the front end sit?', copy:'Start with the real lift or leveling height currently on the truck.' },
        { name:'tire_diameter_in', eyebrow:'Tire target', title:'How much tire do you want?', copy:'Choose the target diameter. Tex will use it with the suspension and wheel dimensions.' },
        { name:'wheel_diameter', eyebrow:'Wheel package', title:'Choose the wheel diameter.', copy:'Wheel diameter affects sidewall, brake clearance and the look of the finished truck.' },
        { name:'wheel_width', eyebrow:'Wheel package', title:'How wide should the wheel be?', copy:'Width works with offset to position the tire. If you are unsure, keep the choice open for the verified fitment.' },
        { name:'stance_preference', eyebrow:'Stance', title:'Where should the tire sit?', copy:'Choose the visual target. We will not trade away brake, suspension or body clearance just to force the stance.' },
        { name:'driving_use', eyebrow:'Real-world use', title:'What does the truck do most?', copy:'Daily use, towing and trail work create different priorities for sidewall and offset.', submit:'Build my wheel brief' }
      ]
    },
    LIGHTING: {
      number: '03', label: 'NIGHT VISION PLAN', title: 'Own the road after the sun drops.',
      stages: [
        { type:'garage', name:'garage_vehicle', eyebrow:'Start with your truck', title:'Which truck are we lighting?', copy:'Confirm the saved Garage vehicle so the mounting and wiring brief stays attached to the right build.' },
        { name:'mounting_location', eyebrow:'Mounting zone', title:'Where should the light live?', copy:'The mounting height and position determine what the beam can actually do.' },
        { name:'beam_use', eyebrow:'Beam behavior', title:'What do you need to see?', copy:'Distance, shoulder coverage and dust performance require different optics.' },
        { name:'road_use', eyebrow:'Environment', title:'Where will you switch it on?', copy:'Road, trail and work-area use each need a different balance of output and control.' },
        { name:'switch_system', eyebrow:'Controls', title:'How will the lights be controlled?', copy:'We will account for the factory auxiliary switches or an aftermarket controller.', submit:'Build my lighting plan' }
      ]
    },
    RECOVERY: {
      number: '04', label: 'RECOVERY BLUEPRINT', title: 'Build the exit before you get stuck.',
      stages: [
        { type:'garage', name:'garage_vehicle', eyebrow:'Start with your truck', title:'Which truck needs a way out?', copy:'Confirm the saved Garage vehicle before we size the recovery system around its real loaded weight.' },
        { name:'loaded_weight_lbs', eyebrow:'Loaded vehicle', title:'What does the truck weigh ready to travel?', copy:'Include people, fuel, tools, bumpers, camping gear and anything normally carried.', input:true },
        { name:'weight_source', eyebrow:'Confidence', title:'Where did that weight come from?', copy:'A scale ticket is strongest; GVWR or a careful estimate can provide a starting point.' },
        { name:'recovery_use', eyebrow:'Recovery load', title:'What kind of recovery do you expect?', copy:'Occasional self-recovery and deep resistance are not the same job.' },
        { name:'mount_status', eyebrow:'Mounting system', title:'Is the truck winch-ready?', copy:'The bumper or mounting plate must be rated for the selected winch.' },
        { name:'line_type', eyebrow:'Line system', title:'Choose the line you want to manage.', copy:'Synthetic rope and steel cable have different handling, inspection and fairlead needs.', submit:'Build my recovery plan' }
      ]
    }
  };

  const fieldControl = (form, name) => form.querySelector(`[name="${name}"]`);
  const fieldValue = (form, stage) => {
    const controls = [...form.querySelectorAll(`[name="${stage.name}"]`)];
    const control = controls[0];
    if (!control) return '';
    if (control.type === 'radio') return controls.find((item) => item.checked)?.value || '';
    return control.value || '';
  };
  const setFieldValue = (form, stage, value) => {
    const controls = [...form.querySelectorAll(`[name="${stage.name}"]`)];
    const control = controls[0];
    if (!control) return;
    if (control.type === 'radio') {
      controls.forEach((item) => { item.checked = item.value === value; });
      controls.find((item) => item.checked)?.dispatchEvent(new Event('change', { bubbles:true }));
    } else {
      control.value = value;
      control.dispatchEvent(new Event('input', { bubbles:true }));
      control.dispatchEvent(new Event('change', { bubbles:true }));
    }
  };
  const stageOptions = (form, stage) => {
    const controls = [...form.querySelectorAll(`[name="${stage.name}"]`)];
    const control = controls[0];
    if (!control || stage.input || ['number','text'].includes(control.type)) return [];
    if (control.type === 'radio') return controls.map((item) => ({ value:item.value, label:stage.labels?.[item.value] || humanize(item.value), meta:stage.meta?.[item.value] || '', icon:stage.icons?.[item.value] || '' }));
    return [...control.options].filter((option) => option.value && option.value !== 'other').map((option, index) => ({ value:option.value, label:option.textContent.trim(), meta:'', icon:String(index + 1).padStart(2,'0') }));
  };

  function createBuildLab(root, form, finderKey, exactControls) {
    const config = LAB_COPY[finderKey];
    if (!config || !form) return;
    root.classList.add('has-build-lab');
    form.classList.add('trm-v11__guided-source');
    const originalVehicle = root.querySelector('[data-trm-v11-vehicle]');

    const lab = document.createElement('section');
    lab.className = 'trm-lab';
    lab.setAttribute('data-trm-lab', '');
    const visual = document.createElement('aside');
    visual.className = 'trm-lab__visual';
    visual.innerHTML = `<div class="trm-lab__scan" aria-hidden="true"></div><p class="trm-lab__visual-label"></p><span class="trm-lab__number"></span><h2></h2><p class="trm-lab__visual-promise">Truck confirmed. Priorities understood. Recommendations explained.</p><div class="trm-lab__vehicle"><small>MY TRUCK GARAGE</small><strong></strong><button type="button">Change truck</button></div><div class="trm-lab__summary" aria-label="Current build selections"></div>`;
    visual.querySelector('.trm-lab__visual-label').textContent = config.label;
    visual.querySelector('.trm-lab__number').textContent = config.number;
    visual.querySelector('h2').textContent = config.title;

    const work = document.createElement('div');
    work.className = 'trm-lab__work';
    work.innerHTML = `<header class="trm-lab__top"><div><small>TRM FITMENT ADVISOR</small><strong data-trm-lab-progress-label></strong></div><div class="trm-lab__meter"><i data-trm-lab-meter></i></div></header><div class="trm-lab__question"><p data-trm-lab-eyebrow></p><h2 data-trm-lab-title></h2><p data-trm-lab-copy></p><div class="trm-lab__options" data-trm-lab-options></div><div data-trm-lab-help></div></div><footer class="trm-lab__nav"><button type="button" class="trm-lab__back" data-trm-lab-back>← Previous</button><button type="button" class="trm-lab__continue" data-trm-lab-continue hidden>Continue →</button></footer>`;
    lab.append(visual, work);
    form.insertAdjacentElement('beforebegin', lab);

    const vehicleStrong = visual.querySelector('.trm-lab__vehicle strong');
    const updateVehicle = () => { vehicleStrong.textContent = vehicleLabel(currentVehicle()); };
    updateVehicle();
    const openGarage = () => originalVehicle?.querySelector('[data-trm-v11-garage]')?.click();
    visual.querySelector('.trm-lab__vehicle button').addEventListener('click', openGarage);

    let index = 0;
    const selectedLabel = (stage) => {
      if (stage.type === 'garage') return vehicleKey(currentVehicle()) ? vehicleLabel(currentVehicle()) : '';
      if (stage.type === 'vehicle') return exactControls?.exactKey() ? exactControls.exactLabel() : '';
      const value = fieldValue(form, stage);
      if (!value) return '';
      return stageOptions(form, stage).find((option) => option.value === value)?.label || humanize(value);
    };
    const renderSummary = () => {
      const summary = visual.querySelector('.trm-lab__summary');
      summary.replaceChildren();
      config.stages.slice(0,index + 1).forEach((stage) => {
        const value = selectedLabel(stage);
        if (!value) return;
        const chip = document.createElement('span');
        chip.innerHTML = `<small></small><strong></strong>`;
        chip.querySelector('small').textContent = stage.eyebrow;
        chip.querySelector('strong').textContent = value;
        summary.append(chip);
      });
    };
    const addBedHelp = (container) => {
      const panel = document.createElement('details');
      panel.className = 'trm-lab__bed-help';
      panel.innerHTML = `<summary>Not sure about the bed length?</summary><div><div class="trm-lab__measure"><span>Cab</span><i></i><span>Closed tailgate</span></div><p>Measure the inside floor from the bulkhead behind the cab to the inside edge of the closed tailgate. Cab style alone is not reliable.</p><label>VIN for Tex (optional)<input type="text" maxlength="17" placeholder="17-character VIN"></label><button type="button">Open Tex for help</button></div>`;
      const sourceVin = form.querySelector('[name="vin"]');
      const input = panel.querySelector('input');
      if (sourceVin) input.value = sourceVin.value;
      input.addEventListener('input', () => { if (sourceVin) sourceVin.value = input.value.toUpperCase(); });
      panel.querySelector('button').addEventListener('click', requestTexChat);
      container.append(panel);
    };
    const renderLoadError = (container, error) => {
      console.error('[TRM Build Lab] A question could not be rendered.', error);
      const message = document.createElement('p');
      message.className = 'trm-lab__load-error';
      message.textContent = 'This question did not load correctly. Go back once or ask Tex for help with your setup.';
      container.replaceChildren(message);
    };
    const garageStageCard = (confirmed, detail) => {
      const card = document.createElement('div');
      card.className = `trm-lab__garage-card${confirmed ? ' is-confirmed' : ''}`;
      card.innerHTML = `<div class="trm-lab__truck-mark" aria-hidden="true"><svg viewBox="0 0 96 48" role="img"><path d="M7 31h6l7-14h42l12 14h10v9H7z"/><circle cx="27" cy="40" r="7"/><circle cx="70" cy="40" r="7"/></svg></div><div><small></small><strong></strong><span></span></div><button type="button"></button>`;
      card.querySelector('small').textContent = confirmed ? 'SAVED IN MY TRUCK GARAGE' : 'YOUR TRUCK STARTS HERE';
      card.querySelector('strong').textContent = vehicleLabel(currentVehicle());
      card.querySelector('span').textContent = detail;
      const button = card.querySelector('button');
      button.textContent = confirmed ? 'Choose a different truck' : 'Add my truck';
      button.addEventListener('click', openGarage);
      return card;
    };
    const renderGarageStage = (container, continueButton) => {
      const confirmed = Boolean(vehicleKey(currentVehicle()));
      container.classList.add('is-vehicle-stage');
      container.append(garageStageCard(confirmed, confirmed
        ? 'We will keep every answer and recommendation attached to this build.'
        : 'Save a vehicle once and every TRM guide can pick it up automatically.'));
      continueButton.hidden = !confirmed;
      continueButton.textContent = 'Use this truck →';
    };
    const renderExactVehicleStage = (container, continueButton) => {
      const confirmed = Boolean(exactControls?.exactKey());
      container.classList.add('is-vehicle-stage');
      container.append(garageStageCard(Boolean(vehicleKey(currentVehicle())), confirmed
        ? 'Manufacturer application found. Review it below, then keep building.'
        : 'We pulled in what we could. Complete the highlighted details to lock the fitment.'));

      const selectorGrid = document.createElement('div');
      selectorGrid.className = 'trm-lab__vehicle-selectors';
      [
        ['exact_year','Year'],
        ['exact_make','Make'],
        ['exact_model','Model'],
        ['exact_trim','Trim'],
        ['exact_drive','Drivetrain']
      ].forEach(([name, labelText]) => {
        const source = fieldControl(form, name);
        const label = document.createElement('label');
        label.textContent = labelText;
        const select = document.createElement('select');
        select.setAttribute('aria-label', labelText);
        if (source) {
          select.innerHTML = source.innerHTML;
          select.value = source.value;
          select.disabled = source.disabled;
          select.addEventListener('change', () => {
            setFieldValue(form, { name }, select.value);
            window.setTimeout(() => render(index, false), 0);
          });
        } else select.disabled = true;
        label.append(select);
        selectorGrid.append(label);
      });
      container.append(selectorGrid);

      const status = document.createElement('div');
      status.className = `trm-lab__fit-status${confirmed ? ' is-confirmed' : ''}`;
      status.innerHTML = `<span aria-hidden="true"></span><div><strong></strong><small></small></div>`;
      status.querySelector('span').textContent = confirmed ? '✓' : '!';
      status.querySelector('strong').textContent = confirmed ? 'Exact application confirmed' : 'A few truck details are still missing';
      status.querySelector('small').textContent = confirmed
        ? exactControls.exactLabel()
        : 'Finish the enabled fields above. The next selector unlocks automatically.';
      container.append(status);
      continueButton.hidden = !confirmed;
      continueButton.textContent = 'Use this exact truck →';
    };
    const render = (nextIndex = index, shouldScroll = true) => {
      index = Math.max(0, Math.min(config.stages.length - 1, nextIndex));
      const stage = config.stages[index];
      const progress = Math.round(((index + 1) / config.stages.length) * 100);
      work.querySelector('[data-trm-lab-progress-label]').textContent = `Question ${index + 1} of ${config.stages.length}`;
      work.querySelector('[data-trm-lab-meter]').style.width = `${progress}%`;
      work.querySelector('[data-trm-lab-eyebrow]').textContent = stage.eyebrow;
      work.querySelector('[data-trm-lab-title]').textContent = stage.title;
      work.querySelector('[data-trm-lab-copy]').textContent = stage.copy;
      const optionsNode = work.querySelector('[data-trm-lab-options]');
      const helpNode = work.querySelector('[data-trm-lab-help]');
      const continueButton = work.querySelector('[data-trm-lab-continue]');
      optionsNode.replaceChildren();
      helpNode.replaceChildren();
      optionsNode.className = 'trm-lab__options';

      if (stage.type === 'garage') {
        renderGarageStage(optionsNode, continueButton);
        work.querySelector('[data-trm-lab-back]').hidden = index === 0;
        renderSummary();
        if (shouldScroll) lab.scrollIntoView({ behavior:'smooth', block:'center' });
        return;
      }
      if (stage.type === 'vehicle') {
        renderExactVehicleStage(optionsNode, continueButton);
        work.querySelector('[data-trm-lab-back]').hidden = index === 0;
        renderSummary();
        if (shouldScroll) lab.scrollIntoView({ behavior:'smooth', block:'center' });
        return;
      }

      let options = [];
      let current = '';
      try {
        options = stageOptions(form, stage);
        current = fieldValue(form, stage);
      } catch (error) {
        renderLoadError(optionsNode, error);
        continueButton.hidden = true;
        return;
      }

      if (stage.input || !options.length) {
        const source = fieldControl(form, stage.name);
        const input = document.createElement('input');
        input.className = 'trm-lab__input';
        input.type = source?.type || 'text';
        input.min = source?.min || '';
        input.max = source?.max || '';
        input.placeholder = source?.placeholder || 'Enter value';
        input.value = current;
        input.addEventListener('input', () => setFieldValue(form, stage, input.value));
        optionsNode.append(input);
        continueButton.hidden = false;
        continueButton.textContent = stage.submit || 'Continue →';
      } else {
        try {
          if (options.length > 12) optionsNode.classList.add('is-compact');
          options.forEach((option) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'trm-lab__option';
            if (option.value === current) button.classList.add('is-selected');
            button.innerHTML = `<b></b><span><strong></strong><small></small></span><em>✓</em>`;
            button.querySelector('b').textContent = option.icon || option.label.slice(0,2).toUpperCase();
            button.querySelector('strong').textContent = option.label;
            button.querySelector('small').textContent = option.meta;
            if (!option.meta) button.querySelector('small').remove();
            button.addEventListener('click', () => {
              setFieldValue(form, stage, option.value);
              optionsNode.querySelectorAll('.trm-lab__option').forEach((item) => item.classList.toggle('is-selected', item === button));
              renderSummary();
              if (index < config.stages.length - 1) window.setTimeout(() => render(index + 1), 180);
              else {
                continueButton.hidden = false;
                continueButton.textContent = stage.submit || 'Build my recommendations';
              }
            });
            optionsNode.append(button);
          });
        } catch (error) {
          renderLoadError(optionsNode, error);
          continueButton.hidden = true;
          return;
        }
        continueButton.hidden = !(index === config.stages.length - 1 && current);
        continueButton.textContent = stage.submit || 'Build my recommendations';
      }
      if (stage.bedHelp) addBedHelp(helpNode);
      work.querySelector('[data-trm-lab-back]').hidden = index === 0;
      renderSummary();
      if (shouldScroll) lab.scrollIntoView({ behavior:'smooth', block:'center' });
    };

    work.querySelector('[data-trm-lab-back]').addEventListener('click', () => render(index - 1));
    work.querySelector('[data-trm-lab-continue]').addEventListener('click', () => {
      const stage = config.stages[index];
      if (stage.type === 'garage' && !vehicleKey(currentVehicle())) { openGarage(); return; }
      if (stage.type === 'vehicle' && !exactControls?.exactKey()) {
        work.querySelector('.trm-lab__vehicle-selectors select:not(:disabled)')?.focus();
        return;
      }
      const source = fieldControl(form, stage.name);
      if (!fieldValue(form, stage) && source?.required) { source.reportValidity(); return; }
      if (index < config.stages.length - 1) render(index + 1);
      else form.requestSubmit();
    });
    form.addEventListener('submit', () => window.setTimeout(() => {
      const results = root.querySelector('[data-trm-v11-results]');
      const decision = root.querySelector('[data-trm-v11-decision]');
      if ((results && !results.hidden) || (decision && !decision.hidden)) lab.hidden = true;
    }, 0));
    root.querySelector('[data-trm-v11-edit]')?.addEventListener('click', () => { lab.hidden = false; render(0); });
    window.addEventListener('trm:vehicle-changed', () => { updateVehicle(); render(0); });
    render(0);
  }

  document.querySelectorAll('[data-trm-v11]').forEach((root) => {
    if (root.dataset.booted === 'true') return;
    root.dataset.booted = 'true';
    const finderKey = root.dataset.finderKey;
    const form = root.querySelector('[data-trm-v11-form]');
    const bedSelect = root.querySelector('[data-trm-v11-bed]');
    const otherBedLabel = root.querySelector('[data-trm-v11-other-bed]');
    const otherBedInput = otherBedLabel?.querySelector('input');
    const exactControls = finderKey === 'BED_COVER' ? setupExactVehicle(root, form) : null;
    const wizardSteps = [...root.querySelectorAll('[data-trm-v11-wizard-step]')];
    const progressItems = [...root.querySelectorAll('[data-trm-v11-progress]')];
    const showWizardStep = (step, moveFocus = false) => {
      if (finderKey !== 'BED_COVER') return;
      const target = Number(step);
      root.dataset.currentStep = String(target);
      wizardSteps.forEach((group) => group.classList.toggle('is-active', Number(group.dataset.trmV11WizardStep) === target));
      progressItems.forEach((item) => {
        const value = Number(item.dataset.trmV11Progress);
        item.classList.toggle('is-active', value === target);
        item.classList.toggle('is-complete', value < target);
      });
      if (moveFocus && target < 4) {
        const heading = root.querySelector(`[data-trm-v11-wizard-step="${target}"] legend`);
        heading?.setAttribute('tabindex', '-1');
        heading?.focus({ preventScroll: true });
        root.querySelector(`[data-trm-v11-wizard-step="${target}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      if (moveFocus && target === 4) root.querySelector('[data-trm-v11-results]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const validateWizardStep = (step) => {
      const group = root.querySelector(`[data-trm-v11-wizard-step="${step}"]`);
      if (!group) return true;
      const controls = [...group.querySelectorAll('input, select, textarea')].filter((control) => !control.disabled && control.type !== 'hidden');
      const invalid = controls.find((control) => !control.checkValidity());
      if (invalid) {
        invalid.reportValidity();
        invalid.focus({ preventScroll: true });
        return false;
      }
      if (Number(step) === 1 && !text(root.querySelector('[data-trm-v11-exact-key]')?.value)) {
        const driveSelect = root.querySelector('[data-trm-v11-drive]');
        driveSelect?.focus({ preventScroll: true });
        return false;
      }
      return true;
    };

    root.querySelectorAll('[data-trm-v11-next]').forEach((button) => {
      button.addEventListener('click', () => {
        const current = Number(root.dataset.currentStep || 1);
        if (validateWizardStep(current)) showWizardStep(Number(button.dataset.trmV11Next), true);
      });
    });
    root.querySelectorAll('[data-trm-v11-back]').forEach((button) => button.addEventListener('click', () => showWizardStep(Number(button.dataset.trmV11Back), true)));
    root.querySelector('[data-trm-v11-edit]')?.addEventListener('click', () => {
      root.querySelector('[data-trm-v11-results]')?.setAttribute('hidden', '');
      root.querySelector('[data-trm-v11-decision]')?.setAttribute('hidden', '');
      showWizardStep(1, true);
    });
    showWizardStep(1);

    const refreshVehicle = () => {
      const vehicle = currentVehicle();
      const label = root.querySelector('[data-trm-v11-vehicle-label]');
      const help = root.querySelector('[data-trm-v11-vehicle-help]');
      const button = root.querySelector('[data-trm-v11-garage]');
      if (label) label.textContent = vehicleLabel(vehicle);
      if (help) help.textContent = vehicleKey(vehicle)
        ? 'Loaded automatically from My Truck Garage.'
        : 'Select a truck once and we’ll remember it across the store.';
      if (button) button.textContent = vehicleKey(vehicle) ? 'Change truck' : 'Choose truck';
      updateGenericBrowse(root, finderKey);
    };

    root.querySelector('[data-trm-v11-garage]')?.addEventListener('click', (event) => {
      window.dispatchEvent(new CustomEvent('trm:garage-open', {
        detail: { trigger: event.currentTarget, source: 'customer-finder-v14' }
      }));
    });

    root.querySelectorAll('[data-trm-v11-tech]').forEach((button) => {
      button.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('trm:tex-open', {
          detail: { source: 'customer-finder-v14', finderKey }
        }));
        requestTexChat();
      });
    });

    bedSelect?.addEventListener('change', () => {
      const isOther = bedSelect.value === 'other';
      if (otherBedLabel) otherBedLabel.hidden = !isOther;
      if (otherBedInput) {
        otherBedInput.required = isOther;
        if (!isOther) otherBedInput.value = '';
      }
    });

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;

      const fields = Object.fromEntries(new FormData(form).entries());
      const vehicle = currentVehicle();
      const resolvedVehicleKey = text(fields.exact_vehicle_key) || vehicleKey(vehicle);
      if (!resolvedVehicleKey) {
        renderDecision(root, 'needs-truck', 'Choose your truck first', 'My Truck Garage supplies the vehicle information that the guide uses to narrow the catalog.');
        root.querySelector('[data-trm-v11-garage]')?.focus();
        return;
      }

      const publicFields = { ...fields };
      delete publicFields.vin;
      const request = {
        version: 'TRM-FINDER-CUSTOMER-V14',
        finderKey,
        vehicleKey: resolvedVehicleKey,
        vehicleLabel: finderKey === 'BED_COVER' ? exactVehicleLabel(fields) : vehicleLabel(vehicle),
        inputs: publicFields,
        vinProvided: Boolean(text(fields.vin)),
        pdpContext: safePdpContext(finderKey),
        requestedAt: new Date().toISOString()
      };
      sessionStorage.setItem(REQUEST_KEY, JSON.stringify(request));
      window.dispatchEvent(new CustomEvent('trm:fitment-request', { detail: request }));

      if (finderKey === 'BED_COVER') {
        const length = selectedBedLength(fields);
        const profile = {
          vehicleKey: resolvedVehicleKey,
          exactVehicleKey: text(fields.exact_vehicle_key),
          garageVehicleKey: vehicleKey(vehicle) || null,
          vehicleLabel: exactVehicleLabel(fields),
          bedLengthIn: length,
          primaryUse: fields.primary_use,
          bedInteraction: fields.bed_interaction,
          savedAt: new Date().toISOString()
        };
        sessionStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
        if (vehicle && /^(?:19|20)\d{2}$/.test(text(fields.exact_year))) {
          try {
            localStorage.setItem(GARAGE_KEY, JSON.stringify({
              ...vehicle,
              exactYear: text(fields.exact_year),
              drivetrain: text(fields.exact_drive) || vehicle.drivetrain || ''
            }));
            window.dispatchEvent(new CustomEvent('trm:vehicle-detail-changed', {
              detail: { exactYear: text(fields.exact_year), drivetrain: text(fields.exact_drive) }
            }));
          } catch (_) {}
        }
        if (text(fields.vin)) {
          sessionStorage.setItem(PRIVATE_KEY, JSON.stringify({ vin: text(fields.vin).toUpperCase(), savedAt: profile.savedAt }));
        } else {
          sessionStorage.removeItem(PRIVATE_KEY);
        }

        const matches = findBedCoverMatches(vehicle, fields);
        renderBedCoverResults(root, matches, fields);
        if (matches.length) {
          const accessoryNote = fields.bed_interaction === 'none'
            ? 'We ranked them by the way you use the bed and included the important tradeoffs.'
            : 'We ranked them by your use; because you have bed hardware, check the rail-space note on each recommendation.';
          renderDecision(root, 'options', `${matches.length} matching option${matches.length === 1 ? '' : 's'} found`, accessoryNote);
          showWizardStep(4, true);
        } else {
          renderDecision(root, 'no-match', 'Let’s double-check the truck and bed length', 'We don’t have a verified catalog intersection for this exact combination yet. Recheck the measured length or ask Tex to help identify the correct cover.');
        }
      } else {
        const recommendation = genericRecommendation(finderKey, fields);
        renderDecision(root, 'summary', recommendation.title, recommendation.copy);
      }
    });

    const recoveryWeight = form?.querySelector('[name="loaded_weight_lbs"]');
    recoveryWeight?.addEventListener('input', () => {
      const output = root.querySelector('[data-trm-v11-recovery-output]');
      const loaded = number(recoveryWeight.value);
      if (!output) return;
      output.textContent = loaded == null
        ? 'Enter the truck’s loaded weight to see a starting winch-capacity range.'
        : `Starting point: at least ${(Math.ceil((loaded * 1.5) / 500) * 500).toLocaleString('en-US')} lb of rated line pull, plus a compatible mount and electrical system.`;
    });

    window.addEventListener('trm:vehicle-changed', refreshVehicle);
    window.addEventListener('trm:vehicle-changed', () => exactControls?.prefill(true));
    window.addEventListener('trm:vehicle-cleared', refreshVehicle);
    window.addEventListener('storage', refreshVehicle);
    refreshVehicle();

    if (finderKey === 'BED_COVER') {
      const profile = read(sessionStorage, PROFILE_KEY);
      if (profile) {
        const option = Array.from(bedSelect?.options || []).find((item) => number(item.value) === number(profile.bedLengthIn));
        if (option) bedSelect.value = option.value;
        const use = form.querySelector(`[name="primary_use"][value="${CSS.escape(profile.primaryUse || '')}"]`);
        if (use) use.checked = true;
        const interaction = form.querySelector('[name="bed_interaction"]');
        if (interaction && profile.bedInteraction) interaction.value = profile.bedInteraction;
      }
    }
    createBuildLab(root, form, finderKey, exactControls);
  });
})();
